<template>
  <div v-if="isPdf">
    <PdfBarsWrapper
      :acc-sof-amount="accSofAmount"
      :acc-sof-price="accSofPrice"
      :acc-uof-amount="accUofAmount"
      :acc-uof-price="accUofPrice"
      :active-data-sources-incoming="activeDataSourcesIncoming"
      :active-data-sources-outgoing="activeDataSourcesOutgoing"
      :show-incoming="showIncoming"
      :show-outgoing="showOutgoing"
      :unique-transactions-incoming="uniqueTransactionsIncoming"
      :unique-transactions-outgoing="uniqueTransactionsOutgoing"
    />
  </div>
  <div
    v-else
    class="report-bars__wrapper"
  >
    <div class="report-bars-control__wrapper">
      <BarsControl
        :active-sort="activeSort"
        :change-for-satoshi="changeForSatoshi"
        :disabled="loading"
        :options-for-sort="optionsForSort"
        @sort-change="sortChange"
      />
    </div>
    <div class="report-bars-main__wrapper">
      <div class="flex fullwidth gap-30 t-flex-wrap">
        <div
          v-show="showIncoming"
          class="report-bars__bar-wrapper"
        >
          <div>
            <RiskExposureHeader
              :amount="accSofAmount"
              :essential="essential"
              header="Source of Funds"
              :loading="loading"
              :price="accSofPrice"
              :transactions="uniqueTransactionsIncoming"
            />
          </div>
          <gl-scoring-bar-chart
            :change-for-satoshi="changeForSatoshi"
            :data-table="activeDataSourcesIncoming"
            :essential="essential"
            :exposure-part="'incoming'"
            :full="showFullBarsIncoming"
            :loading="loading"
            :minimize-bars="isMobile"
            :per-page="barsPerPage"
            :reverse="isMobile"
            :selectable="isBarsSelectable"
          />
          <div
            v-if="showShowMoreButtonIncoming"
            class="flex fullwidth align-center justify-center mt-3"
          >
            <div
              class="gl-button__more-less"
              @click="toggleBarsPerPageIncoming"
            >
              {{ showFullBarsIncoming ? 'Less' : 'More' }}
            </div>
          </div>
        </div>
        <div
          v-show="showOutgoing"
          class="report-bars__bar-wrapper"
        >
          <div>
            <RiskExposureHeader
              :amount="accUofAmount"
              :essential="essential"
              header="Use of Funds"
              :loading="loading"
              :price="accUofPrice"
              :transactions="uniqueTransactionsOutgoing"
            />
          </div>
          <gl-scoring-bar-chart
            :change-for-satoshi="changeForSatoshi"
            :data-table="activeDataSourcesOutgoing"
            :essential="essential"
            :exposure-part="'outgoing'"
            :full="showFullBarsOutgoing"
            :loading="loading"
            :minimize-bars="isMobile"
            :per-page="barsPerPage"
            reverse
            :selectable="isBarsSelectable"
          />
        </div>
      </div>
      <div
        v-if="showShowMoreButtonGeneral"
        class="flex fullwidth align-center justify-center mt-3"
      >
        <div
          class="gl-button__more-less"
          @click="toggleBarsPerPage"
        >
          {{
            (showFullBarsIncoming && isDesktop) || showFullBarsOutgoing
              ? 'Less'
              : 'More'
          }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Vuex
import { mapState, mapGetters } from 'vuex'
// Components
import BarsControl from '@/pages/report/components/gl-report/bars/BarsControl'
import GlScoringBarChart from '@/components/charts/gl-scoring-bar-chart-2'
import RiskExposureHeader from '@/pages/report/components/gl-report/bars/RiskExposureHeader'
import PdfBarsWrapper from '@/pages/report/pdf/components/PdfBarsWrapper'

// Utils
import appConfig from '@/utils/appConfig'
import {
  accumulateValues,
  getUniqueTxLengthFromSource,
} from '@/utils/report-data-formatter'
import { toComaSeparate } from '@/utils/formatNumber'
import { sortingObjects, sourcesSortOrderArray } from '@/utils/sorting'

// Mixins
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'

export default {
  components: {
    BarsControl,
    GlScoringBarChart,
    RiskExposureHeader,
    PdfBarsWrapper,
  },
  mixins: [deviceWidthMixin],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    pureSources: {
      type: Object,
      default: () => ({}),
    },
    allDataSource: {
      type: Object,
      default: () => ({}),
    },
    allDataSourceByOwner: {
      type: Object,
      default: () => ({}),
    },
    selectedToken: {
      type: Object,
      default: () => ({}),
    },
    essential: {
      type: Boolean,
      default: false,
    },
    isPdf: {
      type: Boolean,
      default: false,
    },
    extraPdfParams: {
      type: Object,
      default: () => ({}),
    },
    changeForSatoshi: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      showFullBarsIncoming: false,
      showFullBarsOutgoing: false,
      barsPerPage: 10,
      activeSort: {
        text: 'Highest amount',
        value: 'share',
        key: 'hamount',
        sortOrder: 'desc',
      },
      optionsForSort: [
        {
          text: 'Highest amount',
          value: 'share',
          key: 'hamount',
          sortOrder: 'desc',
        },
        {
          text: 'Lowest amount',
          value: 'amount',
          key: 'lamount',
          sortOrder: 'asc',
        },
        {
          text: 'Highest risk',
          value: 'funds.score',
          key: 'hscore',
          sortOrder: 'desc',
        },
        {
          text: 'Lowest risk',
          value: 'funds.score',
          key: 'lscore',
          sortOrder: 'asc',
        },
        {
          text: 'Label name (desc)',
          value: 'owner',
          key: 'howner',
          sortOrder: 'desc',
        },
        {
          text: 'Label name (asc)',
          value: 'owner',
          key: 'lowner',
          sortOrder: 'asc',
        },
      ],
    }
  },
  computed: {
    ...mapState('analytics', ['coinData']),
    ...mapGetters('directions', [
      'getActivePartBar',
      'getActiveTypeEntityItem',
      'getActiveExposurePart',
      'getExposurePartOptions',
      'getActivePartBarUof',
      'getActivePartBarFull',
      'getActiveExposurePartUof',
      'getActiveExposurePartFull',
      'getActiveTypeEntityItemUof',
      'getActiveTypeEntityItemFull',
    ]),
    ...mapGetters('report', [
      'getPurchasedInfo',
      'getExampleId',
    ]),
    ...mapGetters('user', ['isLogin']),
    appConfig() {
      return appConfig
    },
    activeDataSourcesIncoming() {
      if (this.getActivePartBar.value === 'type') {
        return (
          this.sortingObjects(this.allDataSource?.incoming, this.sortParams) ||
          []
        )
      } else if (this.getActivePartBar.value === 'entity') {
        return (
          this.sortingObjects(
            this.allDataSourceByOwner?.incoming,
            this.sortParams,
          ) || []
        )
      }
      return this.allDataSource?.incoming || []
    },
    activeDataSourcesOutgoing() {
      if (this.getActivePartBar.value === 'type') {
        return (
          this.sortingObjects(this.allDataSource?.outgoing, this.sortParams) ||
          []
        )
      } else if (this.getActivePartBar.value === 'entity') {
        return (
          this.sortingObjects(
            this.allDataSourceByOwner?.outgoing,
            this.sortParams,
          ) || []
        )
      }
      return this.allDataSource?.outgoing || []
    },
    showShowMoreButtonIncoming() {
      return (
        this.activeDataSourcesIncoming.length > this.barsPerPage &&
        this.exposureKey === 'incoming' &&
        this.exposureKey !== 'full' &&
        !this.loading &&
        !Object.keys(this.activeTypeEntityItem).length
      )
    },
    showShowMoreButtonGeneral() {
      return (
        ((Number(this.activeDataSourcesIncoming.length) > this.barsPerPage &&
          this.showIncoming &&
          this.isDesktop) ||
          Number(this.activeDataSourcesOutgoing.length) > this.barsPerPage) &&
        this.showOutgoing &&
        !this.loading &&
        !Object.keys(this.getActiveTypeEntityItem).length
      )
    },
    showIncoming() {
      return this.exposureKey === 'full' || this.exposureKey === 'incoming'
    },
    showOutgoing() {
      return (
        (this.exposureKey === 'full' || this.exposureKey === 'outgoing') &&
        (Number(appConfig.VUE_APP_SCORE_REQUEST_DIRECTION) === 2 ||
          Number(appConfig.VUE_APP_SCORE_REQUEST_DIRECTION) === 1)
      )
    },
    uniqueTransactionsIncoming() {
      return this.allDataSource.evaluatedSofTxs
    },
    uniqueTransactionsOutgoing() {
      return this.allDataSource.evaluatedUofTxs
    },
    accSofPrice() {
      return this.accumulateValues({
        dataSource: this.activeDataSourcesIncoming,
        key: 'amountCur',
      })
    },
    accUofPrice() {
      return this.accumulateValues({
        dataSource: this.activeDataSourcesOutgoing,
        key: 'amountCur',
      })
    },
    accSofAmount() {
      if (this.selectedToken.address === 'all') return ''
      return this.accumulateValues({
        dataSource: this.activeDataSourcesIncoming,
        key: 'amount',
        formatterValue: true,
        coinData: this.coinData,
        selectedToken: this.selectedToken,
      })
    },
    accUofAmount() {
      if (this.selectedToken.address === 'all') return ''
      return this.accumulateValues({
        dataSource: this.activeDataSourcesOutgoing,
        key: 'amount',
        formatterValue: true,
        coinData: this.coinData,
        selectedToken: this.selectedToken,
      })
    },
    sortParams() {
      return [
        { key: this.activeSort?.value, order: this.activeSort?.sortOrder },
        ...sourcesSortOrderArray,
      ]
    },
    exposureKey() {
      return this.getActiveExposurePart.value
    },
    activeTypeEntityItem() {
      return this.getActiveTypeEntityItem
    },
    isBarsSelectable() {
      if (this.isLogin) {
        return this.getPurchasedInfo.full
      }
      if (this.isFullExample) return true
      return false
    },
    isFullExample() {
      return this.getExampleId && this.getExampleId == 'full'
    },
  },
  methods: {
    toComaSeparate,
    sortingObjects,
    accumulateValues,
    getUniqueTxLengthFromSource,
    toggleBarsPerPage() {
      if (this.isDesktop) {
        this.showFullBarsIncoming = !this.showFullBarsIncoming
        this.showFullBarsOutgoing = !this.showFullBarsOutgoing
      } else {
        this.showFullBarsOutgoing = !this.showFullBarsOutgoing
      }
    },
    toggleBarsPerPageIncoming() {
      this.showFullBarsIncoming = !this.showFullBarsIncoming
    },
    sortChange(value) {
      this.activeSort = value
    },
  },
}
</script>

<style>
.report-bars-control__wrapper {
  margin-bottom: 16px;
}
.report-bars__bar-wrapper {
  width: 100%;
  min-width: calc(50% - 15px);
}

.report-bars__wrapper .loader {
  width: calc(100%) !important;
}
</style>
